import { atom, selector } from "recoil";

import axios from "@utils/axios";
import { buildAdminUsersQuery } from "./adminUsersQueryString";
import { IPagination } from "types/api";
import { AdminUserType, AppSettingType, MembershipGroupType, SignupRequestType } from "types/admin";

/**
 * 승인·거절 뒤에 목록을 다시 읽게 하는 손잡이.
 *
 * Recoil selector 는 자기가 읽은 값이 바뀔 때만 다시 돈다. 승인은 서버 상태만
 * 바꾸므로 selector 입장에서는 아무것도 안 바뀐 것이다. 이 값을 올려서 알려 준다.
 * 가입 신청 목록과 사용자 목록이 **둘 다** 이걸 읽는다 — 승인 한 번에 신청은
 * 사라지고 사용자는 하나 늘기 때문이다.
 */
export const adminRefreshTick = atom<number>({ key: "adminRefreshTick", default: 0 });

/**
 * 화면(DataGrid)은 0부터, 서버(fastapi-pagination)는 1부터 센다.
 * 여기 한 곳에서만 +1 해서 그 차이가 화면 코드로 새지 않게 한다.
 */
export const adminUsersPage = atom<number>({ key: "adminUsersPage", default: 0 });

export const adminUsersPageSize = atom<number>({ key: "adminUsersPageSize", default: 25 });

/** 이메일·이름 부분 일치. 서버가 `%` `_` 를 글자로 취급하므로 그대로 넘긴다. */
export const adminUsersQueryText = atom<string>({ key: "adminUsersQueryText", default: "" });

/**
 * 등급 거르기. **서버로 보낸다.**
 *
 * 정렬과 같은 이유다 — 목록이 서버 페이지네이션이라 브라우저에서 거르면 지금 화면에
 * 있는 25명 안에서만 걸린다. 게다가 등급 칸은 `elite` 를 «엘리트» 로 바꿔 그리므로
 * 화면 글자로 거르면 아무것도 안 걸린다.
 *
 * 빈 문자열이면 «전체» 다. `none` 은 유효 등급이 하나도 없는 사람으로, 운영에서는
 * 대다수가 여기 든다.
 */
export const adminUsersLevel = atom<string>({ key: "adminUsersLevel", default: "" });

/**
 * 멤버십 **그룹** 거르기. 등급과 축이 다르다.
 *
 * 같은 `pro` 라도 «ETF 투자바이블» 과 «퀀트 입문반» 은 다른 그룹이고, 목록의
 * «멤버십» 칸이 보여 주는 것이 바로 그 그룹이다. 등급으로만 거를 수 있으면
 * «이 강의를 들은 사람» 을 찾을 방법이 없다.
 *
 * `null` 이 «전체» 다. 빈 문자열을 쓰지 않는 이유는 서버가 `int` 를 받기 때문이다 —
 * `membershipId=` 가 실리면 422 가 된다.
 *
 * **기간이 끝난 그룹도 잡힌다.** 멤버십 관리 화면의 «인원수» 와 기준을 맞춘 것이다
 * (서버 `user/views.py` 주석 참고). 기간이 유효한 사람만 보려면 등급으로 거른다.
 */
export const adminUsersMembershipId = atom<number | null>({
  key: "adminUsersMembershipId",
  default: null,
});

/**
 * 계정 상태(활성·비활성) 거르기. **서버로 보낸다.** `null` 이 «전체» 다.
 *
 * 이 칸이 없던 동안 관리자가 «비활성만» 보려면 표 머리의 컬럼 필터로 손이 갔다. 그 필터는
 * **브라우저가** 지금 화면의 25명 안에서만 걸렀다 — «거르는 척»(2026-09-23 공격 시험 f2-0).
 * 서버는 `isActive` 를 원래 받는다.
 */
export const adminUsersActive = atom<boolean | null>({
  key: "adminUsersActive",
  default: null,
});

/**
 * 정렬 상태. **서버로 보낸다.**
 *
 * 목록이 서버 페이지네이션이라 정렬도 서버가 해야 한다. 클라이언트에서 정렬하면
 * **지금 화면에 있는 25명만** 줄을 세운다 — 운영에는 1,615명이 있어서 "가입일이
 * 가장 이른 사람" 을 찾으려고 눌러도 65페이지 중 1페이지 안에서만 정렬된다.
 * 실측으로 확인했다: 헤더를 눌러도 서버 요청이 한 건도 나가지 않았다.
 *
 * `field` 는 **DB 컬럼명**이다. 서버가 `UserModel.__table__.columns` 로 검증하고
 * 모르는 값이면 조용히 `created_at` 으로 되돌린다. 화면 필드명(`createdAt`)과
 * 다르므로 `UserPanel` 이 보내기 전에 바꿔 준다.
 *
 * `level` 만 예외다. 컬럼이 아니라 유효 멤버십에서 계산하는 값이라 서버가 따로
 * 받아 **등급 순위**로 줄을 세운다 (basic < pro < elite < admin).
 */
export type AdminUsersSort = { field: string; order: "asc" | "desc" };

/** 서버 기본값과 같다. 화면도 이 상태로 시작해야 "정렬 안 된 것처럼" 보이지 않는다. */
export const ADMIN_USERS_DEFAULT_SORT: AdminUsersSort = { field: "created_at", order: "desc" };

export const adminUsersSort = atom<AdminUsersSort>({
  key: "adminUsersSort",
  default: ADMIN_USERS_DEFAULT_SORT,
});

export const adminUsersQuery = selector<IPagination<AdminUserType>>({
  key: "adminUsers",
  get: async ({ get }) => {
    const sort = get(adminUsersSort);
    // ★ 여기서 `get()` 하지 않은 atom 은 **셀렉터의 의존성이 아니다.** 그 값을 바꿔도
    //   이 셀렉터는 다시 돌지 않고, 요청도 안 나간다. 등급 필터가 정확히 그래서
    //   «거르는 척» 만 했다.
    //
    //   `buildAdminUsersQuery` 의 입력 타입은 필드가 전부 필수다. 아래에서 하나라도
    //   빠뜨리면 컴파일이 깨진다 — 그게 이 구조의 전부다.
    const queryString = buildAdminUsersQuery({
      page: get(adminUsersPage),
      size: get(adminUsersPageSize),
      orderBy: sort.field,
      order: sort.order,
      q: get(adminUsersQueryText),
      level: get(adminUsersLevel),
      membershipId: get(adminUsersMembershipId),
      isActive: get(adminUsersActive),
    });
    get(adminRefreshTick);

    const response = await axios.get(`users?${queryString}`);
    return response.data;
  },
});

/**
 * 가입 신청 목록에서 **무엇을 볼 것인가.** 빈 문자열이 «전체» 다.
 *
 * 기본이 «대기» 인 이유 — 예전에는 처리된 것까지 전부 받아서 그대로 그렸다. 승인을
 * 눌러도 그 줄은 «승인» 칩으로 바뀌어 **그 자리에 남았고**, 관리자에게는 «승인했는데
 * 왜 아직 있지» 로 보였다.
 *
 * 그게 거슬리는 정도로 끝나지 않는다. 서버 목록에는 상한이 있다(기본 100·최대 500).
 * 처리된 신청이 그 칸을 다 차지하면 **대기 건이 화면에서 통째로 사라진다.** 실측으로
 * 확인했다 — 처리된 신청 120건을 쌓고 화면이 부르던 그대로(파라미터 없이) 호출하니
 * 받은 100건 중 대기중이 **0건**이었고, 60일 전 대기 건은 어디에도 없었다.
 * 쌓이면 승인 자체를 못 하게 된다.
 */
export const adminSignupRequestStatus = atom<string>({
  key: "adminSignupRequestStatus",
  default: "pending",
});

/**
 * 가입 신청 목록.
 *
 * 상한은 **늘 500(서버 최대)** 으로 부른다. 기본값 100 은 조용히 잘리는데, 잘렸다는
 * 것을 알 방법이 응답에 없다(총계를 안 준다). 받은 수가 상한과 같으면 화면이
 * «더 있을 수 있다» 고 알린다.
 *
 * 메일 링크(`?request=<신청ID>`)가 가리키는 신청은 이 목록에 없을 수 있다 — 이미
 * 처리됐거나 상한 밖이거나. 그건 `adminSignupRequestOne` 이 따로 가져온다.
 */
export const SIGNUP_REQUEST_LIMIT = 500;

export const adminSignupRequestsQuery = selector<SignupRequestType[]>({
  key: "adminSignupRequests",
  get: async ({ get }) => {
    get(adminRefreshTick);
    // ★ 이 `get()` 이 없으면 필터를 바꿔도 셀렉터가 다시 돌지 않는다. 유저 목록의
    //   등급 필터가 정확히 그래서 «거르는 척» 만 했다 (`adminUsersQueryString.ts`).
    const status = get(adminSignupRequestStatus);
    const params = new URLSearchParams({ limit: String(SIGNUP_REQUEST_LIMIT) });
    if (status) params.set("status", status);

    const response = await axios.get(`signup-requests?${params.toString()}`);
    return response.data;
  },
});


/**
 * 메일 링크가 가리키는 신청 하나. `null` 이면 안 부른다.
 *
 * 목록에서 찾지 않고 **서버에 따로 묻는** 이유는 목록에 상한이 있기 때문이다.
 * 며칠 지난 알림 메일을 열면 그 신청은 이미 처리됐고, 처리된 것이 쌓이면 상한
 * 밖으로 밀려나 목록 어디에도 없다. 그때 «찾지 못했습니다» 만 보여 주면 정작
 * 알고 싶은 «누가 언제 처리했나» 를 알려 주지 못한다.
 */
export const adminSignupHighlightId = atom<string | null>({
  key: "adminSignupHighlightId",
  default: null,
});

export const adminSignupRequestOne = selector<SignupRequestType | null>({
  key: "adminSignupRequestOne",
  get: async ({ get }) => {
    const id = get(adminSignupHighlightId);
    if (!id) return null;
    get(adminRefreshTick);
    try {
      const response = await axios.get(`signup-requests/${encodeURIComponent(id)}`);
      return response.data;
    } catch {
      // 지워졌거나 id 가 틀렸다. 화면이 «찾지 못했습니다» 를 띄운다.
      return null;
    }
  },
});

/**
 * 멤버십 그룹 목록. **읽기 전용이다.**
 *
 * 그룹을 만들거나 고치는 API 는 일부러 없다 — 그룹 생성은 `level` 을 고르는 일이고
 * 그건 권한 설계를 바꾸는 행위라서, DB 에서 직접 한다(서버 `membership/views.py` 주석).
 */
export const adminMembershipsQuery = selector<MembershipGroupType[]>({
  key: "adminMemberships",
  get: async ({ get }) => {
    get(adminRefreshTick);
    const response = await axios.get("memberships");
    return response.data;
  },
});


/**
 * 운영 중 바꾸는 설정. **관리자만.**
 *
 * `adminRefreshTick` 을 읽으므로 스위치를 올린 뒤 그 값을 올리면 다시 읽는다.
 * 응답만 믿고 화면 상태를 바꾸지 않는 이유는, 저장이 실패했는데 켜진 것처럼
 * 보이는 상태가 생기기 때문이다 — 이 값들은 그렇게 어긋나면 «막았다» 고 믿는
 * 채로 문이 열려 있게 된다.
 */
export const adminSettingsQuery = selector<AppSettingType[]>({
  key: "adminSettings",
  get: async ({ get }) => {
    get(adminRefreshTick);
    const response = await axios.get("admin/settings");
    return response.data;
  },
});
